import { afterReturn, geotabServicePeriod, geotabVisitTimes, matchGeotabSites, minutesOf } from "./consolidationGeotabFill.js";
import { serviceTimeSource } from "./consolidationPresentation.js";
import { groupFollower } from "./consolidationLoadingGroups.js";

// Heure du papier jugée conforme à Geotab jusqu'à cet écart.
export const PAPER_TOLERANCE_MIN = 10;
const PX_PER_HOUR = { min: 26, max: 60, fallback: 40 };

// Échelle de la colonne : la journée tient dans la hauteur disponible, de 26 à 60 px par heure.
export function pixelsPerHour(start, end, available) {
  if (!available || end <= start) return PX_PER_HOUR.fallback;
  return Math.min(PX_PER_HOUR.max, Math.max(PX_PER_HOUR.min, (available * 60) / (end - start)));
}

// Étiquettes empilées sans se chevaucher : chacune reste à sa hauteur, ou juste sous la précédente.
export function stackLabels(items, line = 17) {
  let floor = -Infinity;
  return items.map(({ want, lines }) => {
    const middle = Math.max(want, floor + line / 2);
    floor = middle + line / 2 + (lines - 1) * line;
    return middle;
  });
}

const fromGeotab = (visit) => visit.geotab || visit.departure_source === "geotab";

// Heure Geotab d'un champ saisi à plus de PAPER_TOLERANCE_MIN de Geotab ; null s'il concorde ou si Geotab ne le connaît pas.
export function paperMismatch(checks, key) {
  const check = checks?.get(key);
  return check && Math.abs(check.gap) > PAPER_TOLERANCE_MIN ? check.geotab : null;
}

// Journée du relevé face à Geotab (minutes depuis minuit du jour du Dispatch) : blocs prévus et réels,
// heures quittées réelles, et heures saisies hors Geotab (papier) avec leur écart à Geotab.
// paperChecks : écart de chaque champ saisi (service_start, service_end, departure:<id>) avec Geotab.
export function sheetGanttModel(row, truck) {
  const plan = truck?.plan;
  const cmp = truck?.comparison;
  if (!plan?.sites?.length || !cmp) return null;
  const period = geotabServicePeriod(truck);
  const real = { start: period.service_start[0] ?? cmp.real_departure, end: period.service_end[0] ?? cmp.end_at, departure: cmp.real_departure, back: cmp.real_return ?? cmp.end_at };
  const planned = { start: minutesOf(row.planned_service_start, row) ?? plan.departure, end: minutesOf(row.planned_service_end, row) ?? plan.return_at, departure: plan.departure, back: plan.return_at };
  const matches = matchGeotabSites(row, truck);
  const times = geotabVisitTimes(row, matches);
  const visitSite = new Map();
  const visitTimes = new Map();
  const pmSites = new Set();
  const papers = new Map();
  const paperChecks = new Map();
  // Trait ▶ : seulement les heures saisies loin de Geotab.
  const ticks = [];
  const paper = (key, id, minutes, geotab) => {
    const gap = minutes - geotab;
    paperChecks.set(key, { geotab, gap });
    if (Math.abs(gap) > PAPER_TOLERANCE_MIN) ticks.push(minutes);
    return { id, minutes, gap };
  };
  row.visits.forEach((visit, index) => {
    const site = matches.get(index);
    if (site) visitSite.set(String(visit.id), site.idx);
    if (visit.pm?.role === "source" || (visit.kind === "delivery" && visit.orders?.some(order => order.pm_loading_reference))) {
      // Le Chargé PM est connu du relevé même si Geotab n'a pas retrouvé cet arrêt.
      const plannedSite = cmp.sites.find(candidate => candidate.kind === visit.kind
        && candidate.order_ids?.some(id => visit.orders?.some(order => order.label === id))) || site;
      if (plannedSite) pmSites.add(plannedSite.idx);
    }
    if (times.has(index)) visitTimes.set(String(visit.id), times.get(index)[1]);
    if (visit.status === "inherited" || visit.pm?.role === "target" || !visit.departure || fromGeotab(visit) || groupFollower(row.visits, visit)) return;
    const minutes = minutesOf(visit.departure, row);
    if (minutes == null || !site || !times.has(index)) return;
    papers.set(site.idx, [...(papers.get(site.idx) || []), paper(`departure:${visit.id}`, String(visit.id), minutes, times.get(index)[1])]);
  });
  const servicePaper = (key, geotab) => {
    if (!row[key] || serviceTimeSource(row, key) || geotab == null) return [];
    const minutes = minutesOf(row[key], row);
    return minutes == null ? [] : [paper(key, key, minutes, geotab)];
  };
  // Journée finie ailleurs qu'au dépôt : son dernier arrêt est dessiné, sans trait de retour. Son départ est inconnu (le
  // rapprochement suppose 60 min) : ni hors plan, ni durée.
  const endStop = cmp.real_return == null && !cmp.in_progress ? cmp.visits.find((v) => v.arr === cmp.end_at && !v.after_day) || null : null;
  const stops = cmp.visits.filter((v) => !v.after_day && v.arr >= cmp.real_departure && (v.arr < cmp.end_at || v === endStop));
  const after = afterReturn(row, truck);
  // Km du trajet qui mène à chaque arrêt réel, y compris les hors-plan et l'après-retour.
  // Ne pas utiliser les trajets comparés : ils peuvent regrouper plusieurs arrêts Geotab.
  const routeStops = cmp.visits.filter(v => v.arr >= real.departure && v.arr <= real.end).sort((a, b) => a.arr - b.arr);
  const distanceLegs = routeStops.flatMap((stop, index) => {
    const from = index ? routeStops[index - 1].dep : real.departure;
    return from != null && stop.arr > from && Number.isFinite(stop.km) && stop.km >= 0
      ? [{ from, to: stop.arr, drive: stop.arr - from, km: stop.km }] : [];
  });
  // Hors plan : 10 min et plus, ou une visite de plus à un lieu de la tournée (v.off).
  const offStops = stops.filter((v) => !v.site_idx && v.kind !== "depot" && v.kind !== "loading" && (v.dwell >= 10 || v.off) && v !== endStop);
  // Changement de camion : « Dépôt » puis les camions, dans l'ordre de la ligne « 2 camions » (trucks_used).
  // Deux lignes : la colonne des étiquettes ne fait que 104 px.
  const used = truck.trucks_used || [];
  const switchLines = (v) => {
    const next = used.findIndex((u) => u.start >= v.arr);
    return [v.kind === "depot" ? "Dépôt" : null, next > 0 ? `${used[next - 1].name} → ${used[next].name}` : "Autre camion"].filter(Boolean);
  };
  // Trajets prévus : du départ au retour, d'un arrêt au suivant ; le dîner prévu n'est pas de la route.
  const planEnds = [{ departure: plan.departure, lunch: 0 }, ...plan.sites.map((site) => ({ arrival: site.arrival, departure: site.departure, lunch: site.lunch_after || 0, km: site.leg_km })), { arrival: plan.return_at, km: plan.return_km }];
  const planLegs = planEnds.slice(1).map((to, i) => ({ from: planEnds[i].departure, to: to.arrival, lunch: planEnds[i].lunch, drive: to.arrival - planEnds[i].departure - planEnds[i].lunch, km: to.km }));
  // Trajets Geotab : d'un arrêt retrouvé au suivant, arrêts imprévus et dîner retirés. Écart au trajet prévu
  // seulement entre deux arrêts consécutifs du Dispatch, dans l'ordre.
  const ends = [{ idx: 0, departure: cmp.real_departure, plan: plan.departure, lunch: 0 },
    ...cmp.sites.filter((site) => site.real_arrival != null).sort((a, b) => a.real_arrival - b.real_arrival)
      .map((site) => ({ idx: site.idx, arrival: site.real_arrival, departure: site.open_end ? null : site.real_departure, planArrival: site.plan_arrival, plan: site.plan_departure, lunch: site.lunch_after || 0 })),
    { idx: cmp.sites.length + 1, arrival: cmp.real_return, planArrival: plan.return_at }];
  const legs = [];
  ends.slice(1).forEach((to, i) => {
    const from = ends[i];
    if (from.departure == null || to.arrival == null || to.arrival <= from.departure) return;
    const stopped = stops.reduce((sum, v) => sum + (v.arr >= from.departure && v.dep <= to.arrival ? v.dep - v.arr : 0), 0);
    const drive = to.arrival - from.departure - stopped;
    const planned = cmp.order_ok !== false && to.idx === from.idx + 1 ? to.planArrival - from.plan - from.lunch : null;
    legs.push({ key: `route-${to.idx}`, from: from.departure, to: to.arrival, stopped, drive, route: planned == null ? null : drive - planned });
  });
  const labels = [
    { key: "service_start", target: "service_start", at: real.start, time: real.start, papers: servicePaper("service_start", real.start) },
    // Lieu retrouvé sans départ connu (dernier arrêt) : ni heure quittée, ni « Non détecté ».
    ...cmp.sites.filter((site) => site.real_departure != null
      || (site.real_arrival == null && !offStops.some((stop) => stop.arr < site.plan_departure && stop.dep > site.plan_arrival)))
      .map((site) => (site.real_departure == null
      ? { key: `site-${site.idx}`, idx: site.idx, at: site.plan_departure, missing: true, papers: [] }
      // Écart sur place : temps réel sur le lieu moins le temps prévu (inconnu pour un arrêt court, partagé ou sans fin).
      : { key: `site-${site.idx}`, idx: site.idx, at: site.real_departure, time: site.real_departure, place: site.real_dwell != null ? site.real_dwell - site.plan_dwell : null,
        offset: site.match === "place" || site.match === "address" ? site.offset_m : null, papers: papers.get(site.idx) || [] })),
    ...legs.filter((leg) => leg.route != null).map((leg) => ({ key: leg.key, at: (leg.from + leg.to) / 2, route: leg.route, papers: [] })),
    ...offStops.filter((v) => !v.switch).map((v) => ({ key: `off-${v.arr}`, at: (v.arr + v.dep) / 2, off: v.dwell, stop: v, papers: [] })),
    ...stops.filter((v) => v.switch).map((v) => ({ key: `switch-${v.arr}`, at: (v.arr + v.dep) / 2, switch: switchLines(v), papers: [] })),
    // Commandes préchargées chargées le matin : chargement normal, sans écart (aucune heure prévue ce jour-là).
    ...stops.filter((v) => v.kind === "loading").map((v) => ({ key: `pre-${v.arr}`, at: v.dep, time: v.dep, papers: papers.get(`L${v.arr}`) || [] })),
    // Après le retour au dépôt : heure du retour, « Quitté à » de chaque arrêt, durée des pauses du carnet.
    ...(after && !after.idle ? [{ key: "after-back", at: after.back, time: after.back, note: "dépôt", papers: [] },
      ...after.stops.filter((s) => (s.type === "stop" || s.type === "pause") && s.dep < after.end).map((s) => (s.type === "pause"
        ? { key: `after-${s.arr}`, at: (s.arr + s.dep) / 2, pause: s.dep - s.arr, papers: [] }
        : { key: `after-${s.arr}`, at: s.dep, time: s.dep, papers: [] }))] : []),
    ...(endStop ? [{ key: "end-stop", at: endStop.arr, time: endStop.arr, note: "fin de journée", papers: [] }] : []),
    { key: "service_end", target: "service_end", at: real.end, time: real.end,
      note: (after || endStop) && period.service_end[1] === "carnet" ? "carnet" : null, papers: servicePaper("service_end", real.end) },
  ].sort((a, b) => a.at - b.at);
  // Axe à la demi-heure autour du prévu et du réel ; une heure tapée hors de cette plage n'élargit pas la colonne.
  const start = Math.floor((Math.min(planned.start, real.start) - 10) / 30) * 30;
  const end = Math.ceil((Math.max(planned.end, real.end) + 10) / 30) * 30;
  // Valeurs déjà reprises de Geotab : ni trait « papier », ni bouton « Reprendre ».
  const geotabFilled = new Set([
    ...row.visits.filter((visit) => visit.departure && fromGeotab(visit)).map((visit) => String(visit.id)),
    ...["service_start", "service_end"].filter((key) => serviceTimeSource(row, key)),
  ]);
  // Chargement Geotab des commandes préchargées sans ligne « Chargé ce jour » dans le relevé : segment orphelin.
  const linked = new Set(visitSite.values());
  const orphanLoads = new Set(stops.filter((v) => v.kind === "loading" && !linked.has(`L${v.arr}`)).map((v) => v.arr));
  // Crochet vert (sameStops = nombre d'arrêts) : chaque arrêt prévu possède une correspondance Geotab.
  // Les lieux éloignés et les autres écarts restent détaillés, sans bloquer le crochet.
  const stopDiffs = {
    total: plan.sites.length,
    found: cmp.sites.filter((site) => site.real_arrival != null).length,
    missing: cmp.sites.filter((site) => site.match === "missing").map((site) => site.name),
    loose: cmp.sites.filter((site) => site.match === "place" || site.match === "address").map((site) => ({ name: site.name, offset: site.offset_m })),
    off: offStops.filter((v) => !v.switch),
    order: cmp.order_ok === false,
    switch: stops.some((v) => v.switch),
    orphans: stops.filter((v) => orphanLoads.has(v.arr)),
  };
  const sameStops = cmp.sites.length === plan.sites.length && stopDiffs.found === cmp.sites.length ? cmp.sites.length : 0;
  return { start, end, planned, real, after, endStop, sites: cmp.sites, orphanLoads, sameStops, stopDiffs, planSites: plan.sites.map((site, index) => ({ ...site, chargedPm: pmSites.has(index + 1) })), stops, labels, legs, planLegs, distanceLegs, ticks: ticks.filter((m) => m >= start && m <= end), visitSite, visitTimes, geotabFilled, paperChecks };
}
